import { Container, Sprite, type Text, type Texture } from "pixi.js";
import type { PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap } from "@slot/game-shell";
import type { FeatureFinishedPort, LocalizationService } from "@slot/game-shell";
import { PanelMotion, type PanelMotionTuning } from "../PanelMotion";
import { TEXT, fitWidth, label, value } from "../textStyles";

/** The card (the art's 900 × 640) with the bonus's total. */
const GEOMETRY = {
  content: { width: 1000, height: 760, y: { desktop: 0, mobile: -200, mobile_landscape: 0 } },
  card: { width: 900, height: 640 },
  title: { y: -190, fontSize: 92, box: { width: 760, height: 120 } },
  amount: { y: 0, fontSize: 140, width: 760 },
  subtitle: { y: 170, fontSize: 52, box: { width: 720, height: 80 } },
} as const;

/**
 * The bonus's end behind `FeatureFinishedPort`: "TOTAL WIN / <total> / HOLD & WIN COMPLETE" on the red card, dismissed by
 * a tap. The title has no extra-spins offer (`hasOffer` false), so the shell never asks for the offer layout.
 */
export class FeatureFinishedView extends Container implements FeatureFinishedPort {
  public readonly hasOffer = false;
  private readonly chrome: ModalChrome;
  private readonly motion: PanelMotion;
  private readonly summaryLabel: Text;

  public constructor(input: { card: Texture | null; viewport: ViewportStore; localization: LocalizationService; runner: PresentationTaskRunner; motion: PanelMotionTuning }) {
    super({ label: "FeatureFinishedPanel" });
    const g = GEOMETRY;
    this.chrome = new ModalChrome({
      label: "simpleRoot",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { ...g.content, limits: { desktop: { maxHeight: 1300 }, mobile: { maxWidth: 1040 }, mobile_landscape: {} } },
    });
    this.addChild(this.chrome);

    const panel = new Container({ label: "Panel" });
    if (input.card) {
      const card = new Sprite({ texture: input.card, anchor: 0.5, label: "Card" });
      card.setSize(g.card.width, g.card.height);
      panel.addChild(card);
    }
    this.summaryLabel = value({ style: TEXT.amount, fontSize: g.amount.fontSize, y: g.amount.y, label: "AmountLabel" });
    panel.addChild(
      label({ localization: input.localization, key: "featurefinished.title", fallback: "TOTAL WIN", style: TEXT.heading(), ...g.title, label: "Title" }),
      this.summaryLabel,
      label({ localization: input.localization, key: "featurefinished.subtitle", fallback: "HOLD & WIN COMPLETE", style: TEXT.body, ...g.subtitle, label: "SubTitle" }),
    );
    this.chrome.content.addChild(panel);
    this.motion = new PanelMotion({ chrome: this.chrome, panel, runner: input.runner, tuning: input.motion });
  }

  public showSimple(): void {
    this.motion.preSeat();
    this.chrome.visible = true;
  }

  public showOffer(): void {
    this.showSimple();
  }

  public hide(): void {
    this.chrome.visible = false;
    this.motion.stop();
  }

  public setSummary(text: string): void {
    this.summaryLabel.text = text ?? "";
    fitWidth(this.summaryLabel, GEOMETRY.amount.width);
  }

  public setCostText(_text: string): void {}

  public setSkipEnabled(enabled: boolean): void {
    this.chrome.skipButton.visible = enabled;
  }

  public setBuyEnabled(_enabled: boolean): void {}

  public setDeclineEnabled(_enabled: boolean): void {}

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.chrome.skipButton, onPress);
  }

  public bindBuyTap(_onPress: () => void): () => void {
    return () => {};
  }

  public bindDeclineTap(_onPress: () => void): () => void {
    return () => {};
  }

  public playEnterAnimation(): Promise<void> {
    return this.motion.enter();
  }

  public playExitAnimation(): Promise<void> {
    return this.motion.exit();
  }

  public fadeInContinueText(): Promise<void> {
    return this.motion.fadeInContinueText();
  }

  public dispose(): void {
    this.chrome.dispose();
    this.destroy({ children: true });
  }
}
